import classnames from 'classnames';
import Link from 'next/link';
import { ReactElement, SyntheticEvent } from 'react';

import { IconProps, LoadingIcon } from '@icons';

import styles from './Button.module.scss';

export type ButtonProps = {
  /**
   * What text should be shown?
   */
  text: string;
  /**
   * Generic onclick handler
   */
  onClick?: (event: SyntheticEvent) => void;
  /**
   * URL to navigate to, this is only required if you want a styled link instead of a button
   */
  href?: string;
  /**
   * Base appearance
   */
  appearance?: 'primary' | 'secondary' | 'tertiary' | 'counter' | 'text';
  /**
   * For appearance `primary`, `secondary` or `tertiary` this will show a slim
   * version of the button
   */
  slim?: boolean;
  small?: boolean;
  /**
   * For appearance `primary`, `secondary` or `tertiary` this will show an inverted design
   * version of the button
   */
  inverted?: boolean;
  disabled?: boolean;
  icon?: (props: IconProps) => ReactElement | ReactElement[];
  /**
   * When using an icon, some SVG icons require `stroke` instead of `fill` to display correctly
   * setting this to true will flip the css rule for the icon
   */
  useStrokeForIcon?: boolean;
  /**
   * Class name for additional styling
   */
  className?: string;
  /**
   * For appearance will show a button the full width of its div
   */
  fullWidth?: boolean;
  isLoading?: boolean;
  testID?: string;
};

export const Button = ({
  text,
  icon: Icon,
  onClick,
  href,
  appearance,
  disabled,
  slim,
  small,
  inverted,
  useStrokeForIcon,
  className,
  fullWidth,
  isLoading,
  testID,
}: ButtonProps) => {
  const appearanceClass =
    appearance && Object.hasOwnProperty.call(styles, appearance)
      ? styles[appearance]
      : styles.primary;

  const buttonClasses = classnames(appearanceClass, className, {
    [styles.disabled]: disabled,
    [styles.slim]: slim,
    [styles.small]: small,
    [styles.inverted]: inverted,
    [styles.fullWidth]: fullWidth,
  });

  const handleClick = (event: SyntheticEvent) => {
    if (!disabled) onClick?.(event);
  };

  const Body = () => (
    <>
      {Icon && (
        <i className={useStrokeForIcon ? styles.useStroke : ''} role="img" aria-label={text}>
          <Icon />
        </i>
      )}
      <span>{text}</span>
      {isLoading && (
        <i className={styles.loadingIcon}>
          <LoadingIcon size={32} />
        </i>
      )}
    </>
  );

  if (href) {
    if (disabled) {
      return (
        <span
          data-testid={testID}
          className={buttonClasses}
          role={href ? 'link' : 'button'}
          aria-disabled>
          <Body />
        </span>
      );
    }
    return (
      <Link
        data-testid={testID}
        onClick={handleClick}
        href={href}
        className={buttonClasses}
        aria-disabled={disabled}>
        <Body />
      </Link>
    );
  }

  return (
    <button
      data-testid={testID}
      type="submit"
      onClick={handleClick}
      className={buttonClasses}
      disabled={disabled || isLoading}>
      <Body />
    </button>
  );
};
